// web/src/components/ds/DsLightbox.tsx — DS.7 (sprint aos-ds-settings-modals,
// spec §2 D6b / D18 / D8b).
//
// `@ds/overlays/Lightbox` is the ONE piece DS.7 mounts that portals
// `[medido at v0.2.0: grep -rln createPortal over the vendored tree -> 6
// files, of which only overlays/Lightbox is in DS.7's set]`, so it is the one
// that needs `portalContainer`: without it the portal lands on
// `document.body`, OUTSIDE `.aos-ds-root`, where every skin role falls back to
// a browser default and the layer stacks above the whole app (D-1462 / CRM TRD
// §13 v10 H5). The container comes from DS.4's `DsPortalProvider` context.
// (`usePortalContainer` reads the same context itself; passing it explicitly
// is what makes D6b's claim auditable at the call site rather than implied.)
//
// WHAT ELSE THE ADAPTER ADDS, and why each exists:
//
//   * TEST IDS. The piece stamps `{...props}` on the portal ROOT only; its
//     backdrop, its centred content box, its `<img>` and its close button
//     have no attribute slot. Five of `ImageLightbox`'s anchors are on those
//     nodes and AC-12 pins all of them, so the adapter stamps them in a mount
//     effect — the same mechanism DS.5's `DsMenu` uses for `role="menu"` and
//     its separators, for the same reason.
//   * A DRAG TRANSFORM HOOK. The swipe-down-to-close gesture belongs to the
//     host (`ImageLightbox`, whose 80 px threshold is "the Cursor / iOS-photos
//     baseline"); the piece has no touch handlers but spreads `{...props}`
//     onto the portal root (Lightbox.tsx:73-80), so the handlers pass through.
//     What does NOT pass through is the live `translateY`/opacity the drag
//     paints on the image — the `<img>` is the piece's own node. `imageStyle`
//     applies it in the same effect.
//   * THE LAYER REGISTRATION (D18).
//
// NOT trapped — the ENUMERATED EXEMPTION of D8b: the lightbox renders exactly
// two focusables (the close button and the image) and its Escape + backdrop
// paths are both covered. Recorded, not engineered around.
//
// ACCEPTED DELTA (D9(d)): the piece always draws its `1 / 1` counter, even for
// a single image. Cosmetic, enumerated in the accepted-diff reasons.

import { useCallback, useContext, useEffect, useRef, type ComponentProps, type CSSProperties } from 'react';
import Lightbox from '@ds/overlays/Lightbox';
import { PortalContainerContext } from '@ds/primitives/portal-container';
import { useAriaHideOutside, useOverlayLayer } from './DsModal';

export interface DsLightboxTestIds {
  /** The dimming layer — clicking it closes. */
  backdrop?: string;
  /** The centred content box that holds the frame. */
  content?: string;
  image?: string;
  close?: string;
}

export interface DsLightboxProps extends Omit<ComponentProps<typeof Lightbox>, 'portalContainer'> {
  /** Stamped on the piece's own nodes, which expose no attribute slot. */
  testIds?: DsLightboxTestIds;
  /** Live style for the `<img>` — the host's drag gesture paints through it. */
  imageStyle?: CSSProperties;
}

export function DsLightbox({
  index,
  onClose,
  testIds,
  imageStyle,
  ...rest
}: DsLightboxProps) {
  const portalContainer = useContext(PortalContainerContext);
  const rootRef = useRef<HTMLDivElement>(null);
  const open = index !== null;

  const isTopmost = useOverlayLayer(open);
  // Radix hid the page behind the preview; the DS piece does not (see DsModal).
  useAriaHideOutside(rootRef, open);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // D18: evaluated at EVENT time, never at render.
  const handleClose = useCallback(() => {
    if (isTopmost()) onCloseRef.current();
  }, [isTopmost]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const kids = root.children;
    if (testIds?.backdrop && kids[0]) kids[0].setAttribute('data-testid', testIds.backdrop);
    const content = kids[kids.length - 1];
    if (testIds?.content && content) content.setAttribute('data-testid', testIds.content);
    const img = root.querySelector('img');
    if (testIds?.image && img) img.setAttribute('data-testid', testIds.image);
    const close = root.querySelector('button');
    if (testIds?.close && close) close.setAttribute('data-testid', testIds.close);
  });

  useEffect(() => {
    const img = rootRef.current?.querySelector('img');
    if (!img) return;
    img.style.transform = imageStyle?.transform ? String(imageStyle.transform) : '';
    img.style.opacity = imageStyle?.opacity !== undefined ? String(imageStyle.opacity) : '';
  }, [imageStyle]);

  return (
    <Lightbox
      ref={rootRef}
      index={index}
      onClose={handleClose}
      portalContainer={portalContainer}
      {...rest}
    />
  );
}

export default DsLightbox;
